import { ImageIcon, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { Spinner } from '@terra/ui';

import { api } from '../lib/api';

/**
 * Logo for a new brand the seller types in with a product. Uploaded to
 * Cloudinary like product photos; the URL goes with the product as
 * `brandLogoUrl`, and the API sets it on the brand only if it has no logo yet.
 */
export function BrandLogoUpload({
  value,
  onChange,
  brandName,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  brandName?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file (PNG, JPG or WebP).');
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const sig = await api.files.getUploadSignature({ folder: 'brand-logos' });
      const form = new FormData();
      form.append('file', file);
      form.append('api_key', sig.apiKey);
      form.append('timestamp', sig.timestamp.toString());
      form.append('signature', sig.signature);
      form.append('folder', sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(err?.error?.message || 'Upload failed');
      }
      const result = (await res.json()) as { secure_url: string };
      await api.files.registerSelf({
        name: file.name,
        url: result.secure_url,
        contentType: file.type,
        sizeBytes: file.size,
        visibility: 'PUBLIC',
      });
      onChange(result.secure_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not upload the logo.');
    } finally {
      setUploading(false);
    }
  };

  const initial = brandName?.trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="mt-2 flex items-center gap-3">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        title={value ? 'Change logo' : 'Upload logo'}
        className={`relative grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-xl border bg-white transition-colors hover:border-amber-400 disabled:opacity-60 ${value ? 'border-slate-200' : 'border-dashed border-slate-300 bg-slate-50'}`}
      >
        {value ? (
          <img src={value} alt="" className="h-full w-full object-contain p-1" />
        ) : (
          <span className="text-xl font-black text-slate-400">{initial}</span>
        )}
        {uploading && (
          <span className="absolute inset-0 grid place-items-center bg-slate-900/40">
            <Spinner size="sm" />
          </span>
        )}
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-ink text-[13px] font-semibold">Brand logo (optional)</p>
        <p className="text-ink-muted text-[11px]">
          Square PNG or JPG. Shown with the brand on the website and app.
        </p>
        <div className="mt-1 flex items-center gap-3 text-xs font-semibold">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1 text-blue-700 hover:underline"
          >
            <ImageIcon className="h-3.5 w-3.5" /> {value ? 'Change logo' : 'Upload logo'}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              disabled={uploading}
              className="inline-flex items-center gap-1 text-red-600 hover:underline"
            >
              <X className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
        {error && <p className="mt-1 text-[11px] font-medium text-red-600">{error}</p>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void upload(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}
